import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "../../App";
import {
  MapPin, Sparkles, Plus, Edit, Trash2,
  Save, X, EyeOff
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AiModelSelect, useAiModel } from "../../components/admin/AiModelSelect";

const Content = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("deals");
  const [deals, setDeals] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [aiModel, setAiModel] = useAiModel();

  const [dealForm, setDealForm] = useState({
    title: "",
    description: "",
    deal_type: "special",
    property_type: "apartment",
    location: "",
    price: "",
    bedrooms: "",
    special_offer: "",
    image_url: "",
    is_active: true
  });

  const [neighborhoodForm, setNeighborhoodForm] = useState({
    name: "",
    description: "",
    vibe: "",
    avg_rent: "",
    highlights: [],
    hidden_catches: [],
    image_url: "",
    is_published: true
  });

  const [highlightInput, setHighlightInput] = useState("");
  const [catchInput, setCatchInput] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dealsRes, neighborhoodsRes] = await Promise.all([
        axios.get(`${API}/deals?active_only=false`),
        axios.get(`${API}/neighborhoods?published_only=false`)
      ]);
      setDeals(dealsRes.data);
      setNeighborhoods(neighborhoodsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
    setLoading(false);
  };

  const openModal = (item = null, type = activeTab) => {
    setEditingItem(item);
    if (type === "deals") {
      setDealForm(item || {
        title: "", description: "", deal_type: "special", property_type: "apartment",
        location: "", price: "", bedrooms: "", special_offer: "", image_url: "", is_active: true
      });
    } else {
      setNeighborhoodForm(item || {
        name: "", description: "", vibe: "", avg_rent: "",
        highlights: [], hidden_catches: [], image_url: "", is_published: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const saveDeal = async () => {
    try {
      if (editingItem?.id) {
        await axios.put(`${API}/deals/${editingItem.id}`, dealForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Deal updated" });
      } else {
        await axios.post(`${API}/deals`, dealForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Deal created" });
      }
      fetchData();
      closeModal();
    } catch {
      toast({ title: "Failed to save deal", variant: "destructive" });
    }
  };

  const saveNeighborhood = async () => {
    try {
      if (editingItem?.id) {
        await axios.put(`${API}/neighborhoods/${editingItem.id}`, neighborhoodForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Neighborhood updated" });
      } else {
        await axios.post(`${API}/neighborhoods`, neighborhoodForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Neighborhood created" });
      }
      fetchData();
      closeModal();
    } catch {
      toast({ title: "Failed to save neighborhood", variant: "destructive" });
    }
  };

  const deleteItem = async (id, type) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/${type}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Item deleted" });
      fetchData();
    } catch {
      toast({ title: "Failed to delete", variant: "destructive" });
    }
  };

  const generateDescription = async (type) => {
    setGenerating(true);
    try {
      if (type === "deal") {
        const response = await axios.post(`${API}/ai/generate`, {
          prompt_type: "deal_description",
          model: aiModel,
          context: {
            property_name: dealForm.title,
            location: dealForm.location,
            features: dealForm.bedrooms,
            special: dealForm.special_offer
          }
        }, { headers: { Authorization: `Bearer ${token}` } });
        setDealForm({ ...dealForm, description: response.data.content });
      } else {
        const response = await axios.post(`${API}/ai/generate`, {
          prompt_type: "neighborhood_guide",
          model: aiModel,
          context: { neighborhood: neighborhoodForm.name }
        }, { headers: { Authorization: `Bearer ${token}` } });
        setNeighborhoodForm({ ...neighborhoodForm, description: response.data.content });
      }
      toast({ title: "Description generated!" });
    } catch {
      toast({ title: "AI generation failed", variant: "destructive" });
    }
    setGenerating(false);
  };

  const addHighlight = () => {
    if (highlightInput.trim()) {
      setNeighborhoodForm({
        ...neighborhoodForm,
        highlights: [...neighborhoodForm.highlights, highlightInput.trim()]
      });
      setHighlightInput("");
    }
  };

  const addCatch = () => {
    if (catchInput.trim()) {
      setNeighborhoodForm({
        ...neighborhoodForm,
        hidden_catches: [...neighborhoodForm.hidden_catches, catchInput.trim()]
      });
      setCatchInput("");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0A2342] font-['Playfair_Display']" data-testid="content-title">
            Content Management
          </h1>
          <p className="text-slate-600">Manage deals and neighborhood guides</p>
        </div>
        <button
          onClick={() => openModal()}
          className="btn-primary flex items-center"
          data-testid="create-content-btn"
        >
          <Plus className="w-5 h-5 mr-2" /> Add {activeTab === "deals" ? "Deal" : "Neighborhood"}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("deals")}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === "deals"
              ? "text-[#DE6E4B] border-b-2 border-[#DE6E4B]"
              : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="tab-deals"
        >
          <Sparkles className="w-4 h-4 inline mr-2" />
          Deals & Specials
        </button>
        <button
          onClick={() => setActiveTab("neighborhoods")}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === "neighborhoods"
              ? "text-[#DE6E4B] border-b-2 border-[#DE6E4B]"
              : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="tab-neighborhoods"
        >
          <MapPin className="w-4 h-4 inline mr-2" />
          Neighborhoods
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2342]"></div>
        </div>
      ) : activeTab === "deals" ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {deals.length === 0 ? (
            <div className="col-span-full bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No deals yet</p>
            </div>
          ) : (
            deals.map((deal) => (
              <motion.div
                key={deal.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-xl border p-6 ${
                  deal.is_active ? "border-slate-200" : "border-red-200 bg-red-50/50"
                }`}
                data-testid={`deal-card-${deal.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className={`badge ${deal.deal_type === "special" ? "bg-orange-100 text-orange-700" : "bg-purple-100 text-purple-700"}`}>
                    {deal.deal_type}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openModal(deal, "deals")}
                      className="p-2 text-slate-400 hover:text-[#0A2342]"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteItem(deal.id, "deals")}
                      className="p-2 text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h3 className="font-semibold text-[#0A2342] mb-1">{deal.title}</h3>
                <p className="text-sm text-slate-500 mb-2 flex items-center">
                  <MapPin className="w-3 h-3 mr-1" /> {deal.location}
                </p>
                {deal.special_offer && (
                  <p className="text-sm text-[#DE6E4B] font-medium">{deal.special_offer}</p>
                )}
                {!deal.is_active && (
                  <span className="text-xs text-red-500 mt-2 block">Inactive</span>
                )}
              </motion.div>
            ))
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {neighborhoods.length === 0 ? (
            <div className="col-span-full bg-white rounded-xl border border-slate-200 p-12 text-center">
              <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No neighborhoods yet</p>
            </div>
          ) : (
            neighborhoods.map((n) => (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-xl border p-6 ${
                  n.is_published ? "border-slate-200" : "border-yellow-200 bg-yellow-50/50"
                }`}
                data-testid={`neighborhood-card-${n.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-[#0A2342] text-lg">{n.name}</h3>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openModal(n, "neighborhoods")}
                      className="p-2 text-slate-400 hover:text-[#0A2342]"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteItem(n.id, "neighborhoods")}
                      className="p-2 text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-2">{n.vibe}</p>
                <p className="text-sm text-[#DE6E4B] font-medium">{n.avg_rent}/mo</p>
                {!n.is_published && (
                  <span className="text-xs text-yellow-600 mt-2 block flex items-center">
                    <EyeOff className="w-3 h-3 mr-1" /> Draft
                  </span>
                )}
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="content-modal"
          >
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0A2342] font-['Playfair_Display']">
                {editingItem ? "Edit" : "Add"} {activeTab === "deals" ? "Deal" : "Neighborhood"}
              </h2>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {activeTab === "deals" ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Title</label>
                      <input
                        type="text"
                        value={dealForm.title}
                        onChange={(e) => setDealForm({ ...dealForm, title: e.target.value })}
                        className="input-field"
                        placeholder="Property name"
                        data-testid="deal-title"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Location</label>
                      <input
                        type="text"
                        value={dealForm.location}
                        onChange={(e) => setDealForm({ ...dealForm, location: e.target.value })}
                        className="input-field"
                        placeholder="Houston, Katy, etc."
                        data-testid="deal-location"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Deal Type</label>
                      <select
                        value={dealForm.deal_type}
                        onChange={(e) => setDealForm({ ...dealForm, deal_type: e.target.value })}
                        className="input-field"
                        data-testid="deal-type"
                      >
                        <option value="special">Special</option>
                        <option value="off-market">Off-Market</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Property Type</label>
                      <select
                        value={dealForm.property_type}
                        onChange={(e) => setDealForm({ ...dealForm, property_type: e.target.value })}
                        className="input-field"
                        data-testid="deal-property-type"
                      >
                        <option value="apartment">Apartment</option>
                        <option value="house">House</option>
                        <option value="duplex">Duplex</option>
                        <option value="multifamily">Multifamily</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Price</label>
                      <input
                        type="text"
                        value={dealForm.price}
                        onChange={(e) => setDealForm({ ...dealForm, price: e.target.value })}
                        className="input-field"
                        placeholder="$1,200 - $1,800"
                        data-testid="deal-price"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Bedrooms</label>
                      <input
                        type="text"
                        value={dealForm.bedrooms}
                        onChange={(e) => setDealForm({ ...dealForm, bedrooms: e.target.value })}
                        className="input-field"
                        placeholder="1-2 Bed"
                        data-testid="deal-bedrooms"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Special Offer</label>
                    <input
                      type="text"
                      value={dealForm.special_offer}
                      onChange={(e) => setDealForm({ ...dealForm, special_offer: e.target.value })}
                      className="input-field"
                      placeholder="1 Month Free + $500 Gift Card"
                      data-testid="deal-special"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-slate-700">Description</label>
                      <div className="flex items-center gap-3">
                      <AiModelSelect value={aiModel} onChange={setAiModel} />
                      <button
                        onClick={() => generateDescription("deal")}
                        disabled={generating}
                        className="text-sm text-[#DE6E4B] hover:text-[#0A2342] flex items-center"
                      >
                        <Sparkles className="w-4 h-4 mr-1" />
                        {generating ? "Generating..." : "Generate with AI"}
                      </button>
                      </div>
                    </div>
                    <textarea
                      value={dealForm.description}
                      onChange={(e) => setDealForm({ ...dealForm, description: e.target.value })}
                      className="textarea-field"
                      rows={4}
                      placeholder="Property description..."
                      data-testid="deal-description"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="deal-active"
                      checked={dealForm.is_active}
                      onChange={(e) => setDealForm({ ...dealForm, is_active: e.target.checked })}
                      className="w-4 h-4"
                      data-testid="deal-active"
                    />
                    <label htmlFor="deal-active" className="text-sm text-slate-700">Active (visible on site)</label>
                  </div>

                  <button onClick={saveDeal} className="btn-primary flex items-center" data-testid="deal-save">
                    <Save className="w-4 h-4 mr-2" /> Save Deal
                  </button>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Neighborhood Name</label>
                      <input
                        type="text"
                        value={neighborhoodForm.name}
                        onChange={(e) => setNeighborhoodForm({ ...neighborhoodForm, name: e.target.value })}
                        className="input-field"
                        placeholder="Houston Downtown"
                        data-testid="neighborhood-name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Average Rent</label>
                      <input
                        type="text"
                        value={neighborhoodForm.avg_rent}
                        onChange={(e) => setNeighborhoodForm({ ...neighborhoodForm, avg_rent: e.target.value })}
                        className="input-field"
                        placeholder="$1,500 - $2,500"
                        data-testid="neighborhood-rent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">The Vibe</label>
                    <input
                      type="text"
                      value={neighborhoodForm.vibe}
                      onChange={(e) => setNeighborhoodForm({ ...neighborhoodForm, vibe: e.target.value })}
                      className="input-field"
                      placeholder="Urban energy with high-rise living..."
                      data-testid="neighborhood-vibe"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-slate-700">Description</label>
                      <div className="flex items-center gap-3">
                      <AiModelSelect value={aiModel} onChange={setAiModel} />
                      <button
                        onClick={() => generateDescription("neighborhood")}
                        disabled={generating}
                        className="text-sm text-[#DE6E4B] hover:text-[#0A2342] flex items-center"
                      >
                        <Sparkles className="w-4 h-4 mr-1" />
                        {generating ? "Generating..." : "Generate with AI"}
                      </button>
                      </div>
                    </div>
                    <textarea
                      value={neighborhoodForm.description}
                      onChange={(e) => setNeighborhoodForm({ ...neighborhoodForm, description: e.target.value })}
                      className="textarea-field"
                      rows={4}
                      placeholder="Detailed neighborhood description..."
                      data-testid="neighborhood-description"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Highlights</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={highlightInput}
                        onChange={(e) => setHighlightInput(e.target.value)}
                        className="input-field flex-1"
                        placeholder="Add a highlight..."
                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addHighlight())}
                      />
                      <button onClick={addHighlight} className="btn-secondary px-4">Add</button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {neighborhoodForm.highlights.map((h, i) => (
                        <span key={`${h}-${i}`} className="badge bg-green-100 text-green-700 flex items-center">
                          {h}
                          <button
                            onClick={() => setNeighborhoodForm({
                              ...neighborhoodForm,
                              highlights: neighborhoodForm.highlights.filter((_, idx) => idx !== i)
                            })}
                            className="ml-1 text-green-500 hover:text-green-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Things to Know (Hidden Catches)</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={catchInput}
                        onChange={(e) => setCatchInput(e.target.value)}
                        className="input-field flex-1"
                        placeholder="Add a catch..."
                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addCatch())}
                      />
                      <button onClick={addCatch} className="btn-secondary px-4">Add</button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {neighborhoodForm.hidden_catches.map((c, i) => (
                        <span key={`${c}-${i}`} className="badge bg-amber-100 text-amber-700 flex items-center">
                          {c}
                          <button
                            onClick={() => setNeighborhoodForm({
                              ...neighborhoodForm,
                              hidden_catches: neighborhoodForm.hidden_catches.filter((_, idx) => idx !== i)
                            })}
                            className="ml-1 text-amber-500 hover:text-amber-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="neighborhood-published"
                      checked={neighborhoodForm.is_published}
                      onChange={(e) => setNeighborhoodForm({ ...neighborhoodForm, is_published: e.target.checked })}
                      className="w-4 h-4"
                      data-testid="neighborhood-published"
                    />
                    <label htmlFor="neighborhood-published" className="text-sm text-slate-700">Published (visible on site)</label>
                  </div>

                  <button onClick={saveNeighborhood} className="btn-primary flex items-center" data-testid="neighborhood-save">
                    <Save className="w-4 h-4 mr-2" /> Save Neighborhood
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Content;
